import { forwardRef, useCallback, useImperativeHandle, useState } from 'react'

import { useUnit } from 'effector-react'

import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Radio, RadioGroup } from '@/components/ui/radio'
import { filterOptions, orderOptions } from '@/consts/chats.ts'
import {
  $chatFilter,
  $chatOrder,
  markAllChatsAsReadFx,
  setChatFilter,
  setChatOrder,
} from '@/models/chattingUI/chats/chats.ts'

import MoreIcon from '../../icons/more.svg'
import { IconButton } from '../components/icon-button'

export interface MoreActionsRef {
  open: () => void
}

export const MoreActions = forwardRef<MoreActionsRef>((_, ref) => {
  const [open, setOpen] = useState(false)
  const filter = useUnit($chatFilter)
  const order = useUnit($chatOrder)
  const markAllChatsAsReadIsLoading = useUnit(markAllChatsAsReadFx.pending)
  const markAllAsRead = useCallback(async () => {
    await markAllChatsAsReadFx()
    setOpen(false)
  }, [])

  useImperativeHandle(
    ref,
    () => ({
      open: () => {
        setOpen(true)
      },
    }),
    [],
  )

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
    >
      <PopoverTrigger
        className="h-5 outline-none"
        asChild
      >
        <IconButton
          tooltip="More"
          icon={MoreIcon}
        />
      </PopoverTrigger>
      <PopoverContent
        side="right"
        align="start"
        alignOffset={-50}
        className="w-[200px] rounded-lg p-2 outline-none"
      >
        <p className="text-secondary-300 px-1.5 text-sm font-medium leading-[22px]">
          Filter
        </p>
        <RadioGroup
          onChange={setChatFilter}
          value={filter}
        >
          {filterOptions.map((option) => (
            <Radio
              className="p-1.5"
              key={option.value}
              labelClassName="text-sm font-medium leading-[22px]"
              {...option}
            />
          ))}
        </RadioGroup>
        <Separator />
        <RadioGroup
          // @ts-ignore
          onChange={setChatOrder}
          value={order}
        >
          {orderOptions.map((option) => (
            <Radio
              className="p-1.5"
              key={option.value}
              labelClassName="text-sm font-medium leading-[22px]"
              {...option}
            />
          ))}
        </RadioGroup>
        <Separator />
        <Button
          onClick={markAllAsRead}
          isLoading={markAllChatsAsReadIsLoading}
          className="bg-card text-title hover:bg-secondary-50 dark:hover:bg-secondary-600 h-max w-full justify-start rounded px-3 py-1.5 text-left text-sm font-medium leading-[22px] shadow-none"
        >
          Mark all as read
        </Button>
        {/* <button className="w-full rounded px-3 py-1.5 text-left text-sm font-medium leading-[22px] text-title hover:bg-secondary-50">
          Select
        </button> */}
      </PopoverContent>
    </Popover>
  )
})

MoreActions.displayName = 'MoreActions'

const Separator = () => <div className="my-2 border-b" />
